import { areObjectIdsEqual } from 'helpers/idComparison';
import { LocalStorageManager } from 'services/storage/LocalStorageManager';
import { showChannelRequestErrorToast } from 'services/toaster';
import { logInfo } from 'setup/logger/logInfo';
import type { ChannelUnsubscribe } from 'shared/channels';
import type { ProjectModel } from 'shared/domain/project/types/model';
import { selectedProjectClient } from 'shared/domain/selectedProject/selectedProjectClient';
import { classifySelectedProjectBroadcast, projectModelToProjectInfo, SelectedProjectChange } from './model';
import type { ProjectSyncActions } from './types';

export type ProjectSelectionActions = {
  /** Switches the worker to the project; a failure is shown and the current project is kept. */
  selectProject: (projectId: string) => Promise<void>;
  /** After a page reload: takes the project the worker has selected, when one was selected before. */
  restoreSelectedProject: () => Promise<void>;
  /** Follows switches of the worker's selected project (also from other tabs); returns the unsubscribe. */
  subscribeToSelectedProjectChanges: () => ChannelUnsubscribe;
};

/** Selection of the project, kept in the worker and mirrored in `store/project`. */
export function createProjectSelectionActions(
  getActions: () => ProjectSyncActions,
  getProjectId: () => string
): ProjectSelectionActions {
  let pendingSelection: { projectId: string; done: Promise<void> } | null = null;

  const switchTo = (project: ProjectModel): void => {
    getActions().setProject(projectModelToProjectInfo(project));
    LocalStorageManager.markProjectSelected();
  };

  const applySelectedProject = (project: ProjectModel | undefined): void => {
    switch (classifySelectedProjectBroadcast(project, getProjectId())) {
      case SelectedProjectChange.refreshMetadata:
        return getActions().patchMetadata(project as ProjectModel);
      case SelectedProjectChange.switchTo:
        return switchTo(project as ProjectModel);
      case SelectedProjectChange.ignore:
        return;
    }
  };

  const runSelection = async (projectId: string): Promise<void> => {
    try {
      switchTo(await selectedProjectClient.select(projectId));
    } catch (error) {
      showChannelRequestErrorToast('Error while selecting a project', error);
    }
  };

  return {
    selectProject: (projectId) => {
      // Effects may ask again before the first switch is marked (auto-select);
      // one worker switch (suspend + flush) per project is enough.
      if (pendingSelection && areObjectIdsEqual(pendingSelection.projectId, projectId)) {
        return pendingSelection.done;
      }
      // `finally` runs after this function returned, once `done` is assigned.
      const done = runSelection(projectId).finally(() => {
        if (pendingSelection?.done === done) {
          pendingSelection = null;
        }
      });
      pendingSelection = { projectId, done };
      return done;
    },

    restoreSelectedProject: async () => {
      if (getProjectId() || !LocalStorageManager.wasProjectSelected()) {
        return;
      }
      try {
        applySelectedProject((await selectedProjectClient.getSelected()) ?? undefined);
      } catch (error) {
        // A timeout is already reported with diagnostics by the channel layer. The
        // project still comes as a `selectedProject` broadcast once the worker has
        // downloaded the project list; until then the user sees the project picker.
        logInfo('selected-project-restore-failed', { error });
      }
    },

    subscribeToSelectedProjectChanges: () => selectedProjectClient.onSelected(applySelectedProject),
  };
}
